import { attributesToProps, type DOMNode, domToReact, type HTMLReactParserOptions } from 'html-react-parser';
import type { ReactNode } from 'react';
import type { JSX } from 'react/jsx-runtime';
import { LinkParser } from '@/app_lib/htmlParser/parsers/anchorParser';
import { DetailParser, SummaryParser } from '@/app_lib/htmlParser/parsers/detailSummaryParser';
import { DivParser } from '@/app_lib/htmlParser/parsers/divParser';
import { FortuneCustomBlock } from '@/app_lib/htmlParser/parsers/FortuneCustomBlock';
import { FigureParser } from '@/app_lib/htmlParser/parsers/figureParser';
import { IframeParser } from '@/app_lib/htmlParser/parsers/iframeParser';
import { ImageParser } from '@/app_lib/htmlParser/parsers/imageParser';
import { OptimizedTableParser } from '@/app_lib/htmlParser/parsers/optimizedTableParser';
import { ScriptParser } from '@/app_lib/htmlParser/parsers/scriptParser';
import { SectionParser } from '@/app_lib/htmlParser/parsers/sectionParser';
import { WidgetParser } from '@/app_lib/htmlParser/parsers/widgetParser';
import { type DomElement, isElement } from '@/app_lib/htmlParser/types';

function transformNode(domNode: DOMNode, reactNode: ReactNode, index: number): ReactNode {
  if (!isElement(domNode)) {
    return reactNode;
  }

  const baseParsedResults = parseBaseComponents(domNode, reactNode, index);
  if (baseParsedResults) {
    return baseParsedResults;
  }

  return reactNode;
}

/**
 * Parses base HTML components and replaces them with custom React components.
 * Returns null for elements that should be ignored (e.g., paywall and tags we don't specify).
 *
 * @param domNode - The DOM node to parse.
 * @param reactNode - The React node representation of the DOM node.
 * @param index - The index of the node in the tree.
 * @returns A JSX element, React node, or null if the element should be ignored.
 */
export function parseBaseComponents(
  domNode: DomElement,
  reactNode: ReactNode,
  index: number,
  tag?: string,
): null | JSX.Element | ReactNode {
  const { name, attribs } = domNode;
  const props = attributesToProps(attribs);
  const { className } = props;

  try {
    if (attribs['data-widget-id']) {
      return (
        <WidgetParser
          domNode={domNode}
          key={index}
          reactNode={reactNode}
        />
      );
    }

    if (attribs['data-ad-id']) {
      return null;
    }

    const ignoreClasses = ['paywall', 'paywall-selector'];
    for (const singleClass of className?.toString().split(' ') || []) {
      if (ignoreClasses.some((ignore) => ignore === singleClass)) {
        return null;
      }

      switch (singleClass) {
        case 'optimized-table-widget':
          if (name !== 'figure') {
            break;
          }
          return (
            <OptimizedTableParser
              domNode={domNode}
              key={index}
              reactNode={reactNode}
            />
          );
        case 'fortune-custom-block':
          return (
            <FortuneCustomBlock
              domNode={domNode}
              key={index}
              reactNode={reactNode}
            />
          );
        default:
          break;
      }
    }

    /* HTML Tag Replacers */
    switch (name) {
      case 'iframe':
        return (
          <IframeParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'img':
        return (
          <ImageParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'a':
        return (
          <LinkParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'details':
        return (
          <DetailParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'summary':
        return (
          <SummaryParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
            tag={tag}
          />
        );
      case 'script':
        return (
          <ScriptParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'figure':
        return (
          <FigureParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'div':
        return (
          <DivParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      case 'section':
        return (
          <SectionParser
            domNode={domNode}
            key={index}
            reactNode={reactNode}
          />
        );
      default:
        return null;
    }
  } catch (error) {
    // biome-ignore lint/suspicious/noConsole: need error
    console.error(`Error parsing node: ${error}`);
    return reactNode;
  }
}

export const options: HTMLReactParserOptions = {
  transform(reactNode: ReactNode, domNode: DOMNode, index: number) {
    //  raw-html-parser types are not up to date
    return transformNode(domNode, reactNode, index) as JSX.Element;
  },
};

// biome-ignore lint/suspicious/noExplicitAny: ts issues
export function renderChild(childNode: (any | DOMNode)[]): ReactNode {
  return domToReact(childNode, options);
}

export const wordpressStyles = {
  'is-type-video':
    'aspect-video h-full [&_*]:aspect-video [&_*]:h-full [.iframe-fallback]:aspect-video [.iframe-fallback]:h-full',
  // This needs to be removed because it's an actual class on some wordpress images that messes with our styling
  'wp-embed-aspect-16-9':
    'aspect-video h-full [&_*]:aspect-video [&_*]:h-full [.iframe-fallback]:aspect-video [.iframe-fallback]:h-full',
};

export function getWordpressStyles(className: string): string[] {
  const classes = className?.toString()?.split(' ') || [];
  return classes.map((cls) => {
    return wordpressStyles[cls];
  });
}
